import * as React from 'react';

import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

import { DataRow, getRowStateKey } from './data-row';
import { useStickyGroupHeader } from './hooks/use-sticky-group-header';
import type { TableBodyBaseProps, TableGroup } from './types';

export interface GroupedTableBodyProps<TData> extends TableBodyBaseProps<TData> {
  groups: TableGroup<TData>[];
  renderGroupHeader: (category: string) => React.ReactNode;
}

/**
 * Body with a header row before each group of rows. The header of the group currently in view
 * sticks below the table header and is pushed away by the next group's header.
 *
 * Groups are assumed to be in the same order as the row model: the rows of group N follow the
 * rows of the groups before it.
 */
export function GroupedTableBody<TData>({
  table,
  groups,
  renderGroupHeader,
  isScrolled,
  onRowClick,
  getRowHref,
  getRowClassName,
  variant,
  tableId,
  filledColumnWidths,
  lastRowChildren,
}: GroupedTableBodyProps<TData>) {
  const { registerHeaderRef, activeIndex, pushOffset } = useStickyGroupHeader(groups.length);
  const rows = table.getRowModel().rows;
  const { columnSizing, columnPinning } = table.getState();
  const columnArrangementKey = table
    .getVisibleLeafColumns()
    .map(column => column.id)
    .join(',');

  let rowOffset = 0;

  return (
    <>
      {groups.map((group, groupIndex) => {
        const groupRows = rows.slice(rowOffset, rowOffset + group.items.length);
        rowOffset += group.items.length;
        const isActive = groupIndex === activeIndex;

        return (
          <React.Fragment key={group.category}>
            <TableRow
              ref={registerHeaderRef(groupIndex)}
              className="border-b-0 border-t-0 hover:bg-muted"
              style={{
                position: isActive ? 'sticky' : 'relative',
                top: isActive ? 'var(--table-header-height, 48px)' : undefined,
                zIndex: isActive ? 9 : undefined,
                transform: isActive && pushOffset < 0 ? `translateY(${pushOffset}px)` : undefined,
              }}
            >
              {/* `overflow-visible` overrides the cell default: an `overflow: hidden` ancestor
                  becomes the containing block for a sticky child, which would pin the label to
                  this cell (never scrolls) instead of to the table's scroll container. */}
              <TableCell
                colSpan={table.getAllColumns().length}
                className={cn('overflow-visible bg-muted py-2')}
              >
                {/* The cell spans every column, so its content starts at the table's left edge and
                    scrolls out of sight horizontally. Pinning keeps the category readable at any
                    scroll offset; `left-4` matches the cell's `first:pl-4`, so the label sits in
                    exactly the same place before scrolling starts. */}
                <div className="sticky left-4 w-fit">{renderGroupHeader(group.category)}</div>
              </TableCell>
            </TableRow>

            {groupRows.map(row => (
              <DataRow<TData>
                key={row.id}
                row={row}
                phase={null}
                hasAnimationWrapper={false}
                rowClassName={getRowClassName?.(row.original)}
                rowStateKey={getRowStateKey(row)}
                columnDefs={table.options.columns}
                columnArrangementKey={columnArrangementKey}
                columnSizing={columnSizing}
                columnPinning={columnPinning}
                filledColumnWidths={filledColumnWidths}
                isScrolled={isScrolled}
                variant={variant}
                tableId={tableId}
                onRowClick={onRowClick}
                getRowHref={getRowHref}
              />
            ))}
          </React.Fragment>
        );
      })}
      {lastRowChildren}
    </>
  );
}
